import { useState, useEffect, useRef } from 'react';
import type { ConflictingMigration } from '../../types';
import checkMigrationConflicts from '../../services/Migration/checkMigrationConflictsService';
import { useAuth } from '../auth';

const DEBOUNCE_MS = 400;
const EMPTY_CONFLICTS: ConflictingMigration[] = [];

export interface UseMigrationConflictsOptions {
  /** Target org ID to check for conflicts */
  targetOrgId: string;
  /** Migration ID to exclude (for edit flow) */
  excludeMigrationId?: string;
  /**
   * When false the hook skips all fetches and returns the empty/idle state.
   * Should be true only when recurring is on and a target org is resolved — for org-to-org
   * that's the selected target org; for division-to-division it's the source org (the target
   * division lives in the same org).
   */
  enabled: boolean;
}

export interface UseMigrationConflictsResult {
  conflicts: ConflictingMigration[];
  hasConflict: boolean;
  loading: boolean;
}

export function useMigrationConflicts({
  targetOrgId,
  excludeMigrationId,
  enabled
}: UseMigrationConflictsOptions): UseMigrationConflictsResult {
  const { accessToken } = useAuth();
  const [conflicts, setConflicts] = useState<ConflictingMigration[]>(EMPTY_CONFLICTS);
  const [hasConflict, setHasConflict] = useState(false);
  const [loading, setLoading] = useState(false);

  // Ref to hold the current AbortController so we can cancel in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled || !targetOrgId) {
      setConflicts(EMPTY_CONFLICTS);
      setHasConflict(false);
      setLoading(false);
      return;
    }

    setLoading(true);

    const timer = setTimeout(() => {
      // Cancel any previous in-flight request
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      checkMigrationConflicts(
        accessToken ?? '',
        targetOrgId,
        excludeMigrationId,
        controller.signal
      ).then((result) => {
        if (controller.signal.aborted) return;
        setConflicts(result.conflictingMigrations);
        setHasConflict(result.hasConflict);
        setLoading(false);
      });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      abortControllerRef.current?.abort();
      setLoading(false);
    };
  }, [enabled, targetOrgId, excludeMigrationId, accessToken]);

  return { conflicts, hasConflict, loading };
}

export default useMigrationConflicts;
